// LB-338 — the Owner's first name, asked once, on an existing household (screen C2).
//
// A household created since LB-337 has a name because the create form will not submit
// without one. Every household made BEFORE that has `ownerName: null` for ever, and nothing
// has ever asked — which is why LB-342's invite email had to fall back to an email's local
// part, and why an adult being invited would be told "jo has invited you" in lower case.
//
// So it is asked here, once, and it cannot be dismissed. Not because a nag is good, but
// because the alternative is a permanent half-state: the name is on every invite email and
// on every attributed row a child reads, and "remind me later" on a one-field form is a
// worse experience than the ten seconds it costs to answer.
//
// It never comes back, and the condition is why: it renders only when the Owner has no name,
// so saving one is the thing that ends it. There is no "seen" flag to get wrong and no way
// for a cleared flag to make it reappear on a household that already answered.
import { t as ct } from "../lib/content.js";

const { useState } = React;

export function OwnerNameSheet({ maxLength = 40, onSave }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const clean = name.trim().replace(/\s+/g, " ").slice(0, maxLength);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-slate-900/60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="owner-name-heading"
      data-testid="owner-name-sheet"
    >
      <div className="w-full bg-white rounded-t-2xl p-4 pb-8" style={{ paddingBottom: "calc(32px + env(safe-area-inset-bottom, 0px))" }}>
        <div className="w-9 h-1 rounded-full bg-slate-200 mx-auto mb-3" />
        <h2 id="owner-name-heading" className="text-lg font-bold text-slate-800 mb-1">
          {ct("adults.ownername_title", "First, what should we call you?")}
        </h2>
        <p className="text-sm text-slate-500 mb-3">
          {ct("adults.ownername_body", "Adults you invite see this name, and it goes on their invite email.")}
        </p>
        <input
          type="text"
          className="border border-slate-200 rounded-lg px-3 py-2.5 text-base w-full mb-3"
          placeholder={ct("adults.ownername_placeholder", "Your first name")}
          aria-label={ct("adults.ownername_placeholder", "Your first name")}
          maxLength={maxLength}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoComplete="given-name"
          autoFocus
          data-testid="owner-name-sheet-input"
        />
        {/* The only way out. No Cancel, no backdrop dismiss, no Escape — see the header. */}
        <button
          className="w-full bg-slate-900 text-white font-bold text-sm rounded-xl py-3 disabled:opacity-40"
          disabled={!clean || busy}
          data-testid="owner-name-sheet-save"
          onClick={async () => { setBusy(true); await onSave(clean); setBusy(false); }}
        >
          {ct("adults.ownername_save", "Save")}
        </button>
      </div>
    </div>
  );
}
